import { useEffect } from 'react';
import type { SceneEffectInstance } from '../../../types/sceneEffects';
import type { StageRuntime } from '../stageRuntime';
import { createEffectManager, type EffectManager } from './effectManagers';

function effectsSignature(effects: SceneEffectInstance[]): string {
  return JSON.stringify(
    effects
      .filter((effect) => effect.isEnabled)
      .map((effect) => ({
        id: effect.id,
        effectType: effect.effectType,
        sorter: effect.sorter,
        properties: effect.properties,
      }))
  );
}

/**
 * Mounts enabled scene effects into the persistent runtime's `effectsLayer`.
 * Opening/closing an effect only adds or removes that layer's children —
 * it never disposes the WebGL renderer.
 */
export function useSceneEffectsRenderer(
  runtime: StageRuntime | null,
  width: number,
  height: number,
  effects: SceneEffectInstance[]
) {
  const signature = effectsSignature(effects);

  useEffect(() => {
    if (!runtime || runtime.isDisposed || width <= 0 || height <= 0) return;

    const enabledEffects = effects.filter((effect) => effect.isEnabled);
    const managers: EffectManager[] = enabledEffects
      .map((effect) => {
        const manager = createEffectManager(effect, runtime.effectsLayer, width, height);
        if (manager) manager.root.renderOrder = effect.sorter;
        return manager;
      })
      .filter((manager): manager is EffectManager => manager != null);

    const stopUpdate =
      managers.length > 0
        ? runtime.addUpdate((delta) => {
            managers.forEach((manager) => manager.update(delta));
          })
        : () => {};

    runtime.syncLoop();

    return () => {
      stopUpdate();
      managers.forEach((manager) => manager.dispose());
      if (!runtime.isDisposed) runtime.syncLoop();
    };
    // `signature` captures enabled effect identity/params; `effects` is read from the closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtime, width, height, signature]);
}
